/** MCP sign-in (the OAuth login handoff) and the user's own MCP connections — backend
 *  metaplan A7b/A7c, AIE plan §6 and §10. Contract: `../apiv2/authserver.openapi.yaml` (v1),
 *  mirrored into console.openapi.yaml as `/oauth/*` (`oauth.contract.test.ts` pins the mirror).
 *
 *  - `POST /oauth/handoff/ {req}` → `{code, expires_in, redirect_url, req_id}`: the route
 *    `/auth/oauth-handoff` posts the authorization server's signed request once the user has
 *    a console session, then sends the browser to `redirect_url`.
 *  - `GET /oauth/connections/` → `{connections, mcp_addresses?, mcp_eligible}`: the user's own
 *    grants for `tids=ops` (all-tenant and per-tenant ops grants), newest first, each naming its
 *    `route`, MCP address (`resource`), bound tenant now (`bound_tenant_id`, `bound_tenant_code`,
 *    `bound_tenant_name` — show these, never parse `resource`) and `stale` (the address no longer
 *    works); `mcp_addresses` = both ops addresses from the backend's configuration (`ops`, and
 *    `ops_tenant` as a `{tenant}` template), only while `mcp_eligible` (the user can connect now).
 *    `DELETE /oauth/connections/{grant_id}/` → 204: the backend finds the grant among them and
 *    revokes it under the grant's `tid`.
 *
 *  Every refusal is the M9 envelope `{code, message, doc_url, request_id}` (not the console's
 *  `{detail}`): ConsoleApiError keeps it as `body`, so branch on `oauthErrorCode()`.
 *  `req` and the minted code are credentials — call `postHandoff` directly, never through
 *  `useMutation`/`useQuery`, whose caches keep variables and data. */
import { apiDelete, apiGet, apiPost, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type OAuthHandoffResponse = Schemas['OAuthHandoffResponse'];
export type OAuthConnection = Schemas['OAuthConnection'];
export type OAuthConnectionList = Schemas['OAuthConnectionList'];
export type OAuthMcpAddress = Schemas['OAuthMcpAddress'];
export type OAuthErrorEnvelope = Schemas['OAuthErrorEnvelope'];

export function postHandoff(req: string): Promise<OAuthHandoffResponse> {
  return apiPost('/oauth/handoff/', { req });
}

export function listConnections(): Promise<OAuthConnectionList> {
  return apiGet('/oauth/connections/');
}

export function revokeConnection(grantId: string): Promise<void> {
  return apiDelete(`/oauth/connections/${encodeURIComponent(grantId)}/`);
}

/** The envelope `code` of a refused `/oauth/*` call; null for a network error or a DRF body
 *  (a 401 for a missing or expired session). Not a type predicate (see isStatementTimeout). */
export function oauthErrorCode(e: unknown): string | null {
  return e instanceof ConsoleApiError && typeof e.body?.code === 'string' ? e.body.code : null;
}

/** The URL the browser may leave for after a handoff: the authorization server's callback as the
 *  backend built it. Only an absolute `https:` URL passes (`http:` too where `allowHttp`, i.e.
 *  local development); anything else — a `javascript:` URL, a relative path, garbage — is null,
 *  so the page refuses instead of navigating. */
export function handoffRedirectTarget(raw: unknown, { allowHttp = false } = {}): string | null {
  if (typeof raw !== 'string') return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol === 'https:' || (allowHttp && url.protocol === 'http:')) return url.toString();
  return null;
}
